/* eslint-disable no-undef */
import React from 'react';
import ColorHash from 'color-hash';
import get from 'lodash.get';
import isEmpty from 'lodash.isempty';
import {
    HiOutlineCalendarDays,
    HiOutlineBuildingOffice2,
    HiBars3BottomLeft,
    HiOutlineIdentification
} from 'react-icons/hi2';
import {IoCheckboxOutline} from '@react-icons/all-files/io5/IoCheckboxOutline';
import {FiType} from '@react-icons/all-files/fi/FiType';
import {IoIosArrowDropdown} from '@react-icons/all-files/io/IoIosArrowDropdown';
import {IoMdRadioButtonOn} from '@react-icons/all-files/io/IoMdRadioButtonOn';
import {HiOutlineUser} from '@react-icons/all-files/hi/HiOutlineUser';
import {BiLabel} from '@react-icons/all-files/bi/BiLabel';
import {deltaE} from 'chroma-js';
import Signature2 from '../shared/icons/Signature2';
import {
    INHERIT_SIZE,
    FIELD_TYPES,
    PACKAGE_TYPE_NAME,
    TEMPLATE_PATH,
    TRANSACTION_PATH,
    RECIPIENT_TYPES
} from '../../../constants';
import {APPROVAL_TYPES} from '@api/constants';
import {getDefaultSignatureType} from './FieldsPanel/Fields/constants';
import {getPageFields} from './designerSlice';
import {getIsSenderRecipient} from '../Packages/Recipients/utils';

export const DEFAULT_WIDTH = 180; // px
export const DEFAULT_HEIGHT = 40; // px
export const DEFAULT_SQUARE_WIDTH = 20; // px
export const DEFAULT_SQUARE_HEIGHT = DEFAULT_SQUARE_WIDTH;

export const DEFAULT_DATE_FIELD_VALUE = 'DD-MM-YYYY';

export const DATE_SETTINGS_LIST = [
    {
        key: 'DMY',
        label: 'esl.generic.date_format_DD_MM_YYYY',
        value: DEFAULT_DATE_FIELD_VALUE
    },
    {
        key: 'YMD',
        label: 'esl.generic.date_format_YYYY_MM_DD',
        value: 'YYYY-MM-DD'
    },
    {
        key: 'YDM',
        label: 'esl.generic.date_format_YYYY_DD_MM',
        value: 'YYYY-DD-MM'
    },
    {
        key: 'MDY',
        label: 'esl.generic.date_format_MM_DD_YYYY',
        value: 'MM-DD-YYYY'
    }
];

const FIELDS_DATA = {
    signature: {
        label: 'page.designer.fields.clickToSign',
        icon: <Signature2 />,
        name: 'fullname-signature',
        typeName: FIELD_TYPES.SIGNATURE
    },
    initials: {
        label: 'page.designer.fields.clickToInitial',
        icon: <Signature2 />,
        name: 'initials-signature',
        typeName: FIELD_TYPES.SIGNATURE
    },
    paraph: {
        label: 'page.designer.fields.graphicalInitial',
        icon: <Signature2 />,
        name: 'paraph-signature',
        typeName: FIELD_TYPES.SIGNATURE
    },
    capture: {
        label: 'page.designer.fields.capture',
        icon: <Signature2 />,
        name: 'capture-signature',
        typeName: FIELD_TYPES.SIGNATURE
    },
    mobile_capture: {
        label: 'page.designer.fields.capture',
        icon: <Signature2 />,
        name: 'capture-signature',
        typeName: FIELD_TYPES.SIGNATURE
    },
    'label-signing_date': {
        label: 'esl.spot.menu.datafield.signing_date.label',
        icon: <HiOutlineCalendarDays />,
        name: 'signing-date',
        typeName: FIELD_TYPES.RECIPIENT
    },
    'label-signer_name': {
        label: 'esl.generic.name',
        icon: <HiOutlineUser />,
        name: 'signer-name',
        typeName: FIELD_TYPES.RECIPIENT
    },
    'label-signer_title': {
        label: 'esl.generic.title',
        icon: <HiOutlineIdentification />,
        name: 'title',
        typeName: FIELD_TYPES.RECIPIENT
    },
    'label-signer_company': {
        label: 'esl.generic.company',
        icon: <HiOutlineBuildingOffice2 />,
        name: 'company',
        typeName: FIELD_TYPES.RECIPIENT
    },
    label: {
        label: 'esl.spot.menu.datafield.labelfield.label',
        icon: <BiLabel />,
        name: 'label',
        typeName: FIELD_TYPES.FORM
    },
    date: {
        label: 'page.designer.fields.date',
        icon: <HiOutlineCalendarDays />,
        name: 'date',
        typeName: FIELD_TYPES.FORM
    },
    textfield: {
        label: 'page.designer.fields.singleLine',
        icon: <FiType />,
        name: 'single-field',
        typeName: FIELD_TYPES.FORM
    },
    text: {
        label: 'page.designer.fields.singleLine',
        icon: <FiType />,
        name: 'single-field',
        typeName: FIELD_TYPES.FORM
    },
    textarea: {
        label: 'page.designer.fields.multiLine',
        icon: <HiBars3BottomLeft />,
        name: 'multi-line',
        typeName: FIELD_TYPES.FORM
    },
    list: {
        label: 'page.designer.fields.dropdown',
        icon: <IoIosArrowDropdown />,
        name: 'dropdown',
        typeName: FIELD_TYPES.FORM
    },
    radio: {
        label: 'page.designer.fields.radioButton',
        icon: <IoMdRadioButtonOn />,
        name: 'radio',
        typeName: FIELD_TYPES.FORM
    },
    checkbox: {
        label: 'esl.spot.menu.datafield.checkbox.label',
        icon: <IoCheckboxOutline />,
        name: 'checkbox',
        typeName: FIELD_TYPES.FORM
    },
    custom: {
        label: '',
        icon: <FiType />,
        name: 'custom-field',
        typeName: FIELD_TYPES.FORM
    },
    paragraph: {
        label: 'page.designer.fields.multiLine',
        icon: <HiBars3BottomLeft />,
        name: 'multi-line',
        typeName: FIELD_TYPES.FORM
    }
};

const API_FIELD_TYPES_LOWER = new Set([
    'signature', 'initials', 'capture', 'mobile_capture', 'seal',
    'label', 'date', 'textfield', 'text', 'textarea',
    'list', 'radio', 'checkbox', 'custom', 'paragraph'
]);

function getApiFieldKey({type, binding, signing_type: signingType}) {
    if (signingType) {
        const apiSigningType = String(signingType).toLowerCase();
        if (FIELDS_DATA[apiSigningType]) return apiSigningType;
    }
    if (!type) return null;
    const apiType = String(type).toLowerCase();
    if (!API_FIELD_TYPES_LOWER.has(apiType)) return null;
    if (binding) {
        const apiBinding = String(binding).toLowerCase();
        const composite = `${apiType}-${apiBinding}`;
        if (FIELDS_DATA[composite]) return composite;
    }
    return apiType;
}

const defaultField = {
    type: '',
    name: '',
    value: '',
    binding: '',
    height: 0,
    width: 0,
    top: 0,
    left: 0,
    page: 0,
    font_size: INHERIT_SIZE,
    is_required: false,
    max_length: null,
    format: null,
    group: null,
    accepted_values: null
};

const defaultApproval = {
    fields: [],
    recipient_id: ''
};
export const MIN_FIELD_MAX_LENGTH = 1;
export const MAX_FIELD_MAX_LENGTH = 4000;
export const DEFAULT_FIELD_MAX_LENGTH_VALUE = MAX_FIELD_MAX_LENGTH;

function getFieldById(approvals = [], fieldId) {
    const allFields = approvals.map(({fields}) => fields).flat();
    return allFields.find(({id}) => id === fieldId);
}

export const colorArray = [
    '#7b6e6e',
    '#f59eff',
    '#fdacd7',
    '#8379db',
    '#4533e6',
    '#f032e6',
    '#aaffc3',
    '#42ac61',
    '#bfef45',
    '#79c027',
    '#469990',
    '#32f049',
    '#1f3193',
    '#4363d8',
    '#78cdff',
    '#16dbc9',
    '#ffb59d',
    '#ffd8b1',
    '#fffbaa',
    '#ffc44e',
    '#f26522',
    '#ff646a',
    '#e6194b',
    '#800000',
    '#dbc899',
    '#9a6324',
    '#808000',
    '#000'
];

function generateColorById(uuid) {
    if (!uuid) {
        return '#aaa';
    }
    const colorHash = new ColorHash({
        lightness: [0.7],
        saturation: [0.8]
    });

    return colorHash.hex(uuid);
}

function getColorsForSigners(signers) {
    if (isEmpty(signers)) {
        return [];
    }
    const newSigners = signers.reduce((prevSigners, signer) => {
        const idColor = generateColorById(signer.recipientId);

        const usedSignersColors = prevSigners.map(({color}) => color);
        const filteredColors = colorArray.filter(
            (c) => !usedSignersColors.includes(c)
        );
        if (!filteredColors.length) {
            return [...prevSigners, {...signer, color: idColor}];
        }

        const newColorsResults = filteredColors.map((color) => ({
            color,
            diff: deltaE(idColor, color)
        }));

        let closestColor = {diff: 100};

        newColorsResults.forEach((color) => {
            if (closestColor.diff > color.diff) {
                closestColor = color;
            }
        });

        return [...prevSigners, {...signer, color: closestColor.color}];
    }, []);

    return newSigners;
}

function getRecipientById({recipients, recipientId}) {
    return recipients.find(({id}) => id === recipientId) || [];
}

function getRecipientIndexById({recipients, recipientId}) {
    return recipients.findIndex(({id}) => id === recipientId);
}

// tag with index 0 is equal to z-index: 0, index 1 to z-index: 1 etc...
// reverse was used to make fields z-index order to be simmiliar to old signer ui
function getTags({recipients, approvals, fieldsByApprovalId = {}, pageIndex}) {
    const reversedApprovals = approvals.filter((a) => a.confirmed_at == null).reverse();
    const tags = reversedApprovals
        .flatMap((approval) => {
            const recipient = getRecipientById({recipients, recipientId: approval.recipient_id});
            const roleIndex = getRecipientIndexById({recipients, recipientId: approval.recipient_id});
            const approvalIndex = reversedApprovals.findIndex(({id}) => id === approval.id);

            const linkedFields = (fieldsByApprovalId[approval.id] || [])
                .filter((field) => field.page === pageIndex);

            if (approval.signing_type && approval.signing_type !== 'accept') {
                const tagsForApproval = [];
                if (approval.page === pageIndex) {
                    const virtualField = {
                        id: approval.id,
                        signing_type: approval.signing_type,
                        page: approval.page,
                        top: approval.top,
                        left: approval.left,
                        width: approval.width,
                        height: approval.height,
                        font_size: approval.font_size,
                        is_optional: approval.is_optional,
                        isSignatureApproval: true
                    };
                    tagsForApproval.push({
                        field: virtualField,
                        recipient,
                        roleIndex,
                        approval,
                        fieldIndex: 0,
                        approvalIndex
                    });
                }
                linkedFields.forEach((field, idx) => {
                    tagsForApproval.push({
                        field,
                        recipient,
                        roleIndex,
                        approval,
                        fieldIndex: idx + 1,
                        approvalIndex
                    });
                });
                return tagsForApproval;
            }

            return linkedFields.map((field, fieldIndex) => ({
                field,
                recipient,
                roleIndex,
                approval,
                fieldIndex,
                approvalIndex
            }));
        });

    return tags;
}

function getConvertedFieldPositionFromResize(target) {
    let left = target.getBoundingClientRect().left
      - target.parentElement.getBoundingClientRect().left;
    const right = target.getBoundingClientRect().right
      - target.parentElement.getBoundingClientRect().right;
    const top = target.getBoundingClientRect().top
      - target.parentElement.getBoundingClientRect().top;
    const docScale = target.parentElement.getBoundingClientRect().width
      / parseFloat(target.parentElement.getAttribute('data-page-width'));

    // Catch left positions under 0
    left = left < 0 ? 0 : left;
    // Catch right positions over 0
    left = right > 0 ? left - right : left;

    return {
        left: Math.round(left / docScale),
        top: Math.round(top / docScale)
    };
}

function getConvertedFieldDimensionFromResize(target) {
    const {width, height} = target.getBoundingClientRect();
    const docScale = target.parentElement.getBoundingClientRect().width
      / parseFloat(target.parentElement.getAttribute('data-page-width'));
    const parentHeight = target.parentElement.getBoundingClientRect().height / docScale;
    const calculatedPosition = getConvertedFieldPositionFromResize(target);
    const calculatedDimensions = {
        width: Math.round(width / docScale),
        height: Math.round(height / docScale)
    };

    // If field is beyond bottom, reduce height
    calculatedDimensions.height = calculatedPosition.top + calculatedDimensions.height > parentHeight - 1
        ? calculatedDimensions.height
          - Math.ceil(calculatedPosition.top + calculatedDimensions.height - parentHeight + 1)
        : calculatedDimensions.height;

    return {
        width: Math.round(calculatedDimensions.width),
        height: Math.round(calculatedDimensions.height)
    };
}

function modifyFieldFromResize(field, target) {
    const options = {
        ...getConvertedFieldPositionFromResize(target),
        ...getConvertedFieldDimensionFromResize(target)
    };
    const updatedField = {...field, ...options};

    return updatedField;
}

function getConvertedFieldPosition(event) {
    const el = event.relatedTarget ? event.relatedTarget : event.target;
    const parentElement = event.relatedTarget
        ? event.target
        : event.target.parentElement;
    const elRect = el.getBoundingClientRect();
    const parentRect = parentElement.getBoundingClientRect();
    let left = elRect.left - parentRect.left;
    const right = elRect.right - parentRect.right;
    let top = elRect.top - parentRect.top;
    const docScale = parentRect.width / parseFloat(parentElement.getAttribute('data-page-width'));
    const parentHeight = parseFloat(parentElement.getAttribute('data-page-height'));
    const blockHeight = elRect.height / docScale;

    // When changing page, catch and inverse top value as it becomes negative
    top = top < 0 ? -top : top;

    // Catch left positions under 0
    left = left < 0 ? 0 : left;
    // Catch right positions over 0
    left = right > 0 ? left - right : left;

    const calculatedPosition = {
        left: Math.round(left / docScale),
        top: Math.round(top / docScale)
    };

    // If field is beyond bottom
    calculatedPosition.top = calculatedPosition.top + blockHeight > parentHeight
        ? parentHeight - blockHeight - 1
        : calculatedPosition.top;

    return calculatedPosition;
}

function getConvertedFieldDimension(event) {
    const el = event.relatedTarget ? event.relatedTarget : event.target;
    const parentElement = event.relatedTarget
        ? event.target
        : event.target.parentElement;
    const {width, height} = el.getBoundingClientRect();
    const docScale = parentElement.getBoundingClientRect().width
      / parseFloat(parentElement.getAttribute('data-page-width'));

    return {
        width: Math.round(width / docScale),
        height: Math.round(height / docScale)
    };
}

function modifyFieldFromDrop(field, event) {
    const options = {
        page: parseInt(event.target.getAttribute('data-page-index'), 10),
        ...getConvertedFieldPosition(event),
        ...getConvertedFieldDimension(event)
    };
    const updatedField = {...field, ...options};

    return updatedField;
}

export function getFieldKey({type, binding, signing_type: signingType} = {}) {
    return getApiFieldKey({type, binding, signing_type: signingType}) || '';
}

export function getFieldLabel(args = {}) {
    const key = getFieldKey(args);
    return (FIELDS_DATA[key] && FIELDS_DATA[key].label) || '';
}
export function getFieldName(args = {}) {
    const key = getFieldKey(args);
    return (FIELDS_DATA[key] && FIELDS_DATA[key].name) || '';
}
function getFieldTypeName(args = {}) {
    const key = getFieldKey(args);
    return (FIELDS_DATA[key] && FIELDS_DATA[key].typeName) || '';
}

export function getApprovalFromPackage({
    approvalsByDocumentId = {}, documentId, approvalId
}) {
    const approvals = approvalsByDocumentId[documentId] || [];
    return approvals.find(({id}) => id === approvalId) || {};
}

export function getAllPackageFields({approvalsByDocumentId = {}, fieldsByApprovalId = {}}) {
    const arr = [];
    Object.entries(approvalsByDocumentId).forEach(([documentId, approvals]) => {
        approvals.forEach((approval) => {
            const fields = fieldsByApprovalId[approval.id] || [];
            fields.forEach((field) => arr.push({
                documentId,
                approvalId: approval.id,
                recipientId: approval.recipient_id,
                ...field
            }));

            const isAcceptApproval = approval.signing_type?.toLowerCase() === APPROVAL_TYPES.ACCEPT;
            if (!isAcceptApproval) {
                arr.push({
                    documentId,
                    approvalId: approval.id,
                    recipientId: approval.recipient_id,
                    id: approval.id,
                    name: approval.name,
                    signing_type: approval.signing_type,
                    isSignatureApproval: true,
                    type: approval.signing_type?.toLowerCase()
                });
            }
        });
    });
    return arr;
}

export function getAllPackageApprovals(approvalsByDocumentId = {}) {
    const arr = [];
    Object.entries(approvalsByDocumentId).forEach(([documentId, approvals]) => {
        approvals.forEach((approval) => arr.push({documentId, ...approval}));
    });
    return arr;
}

export function getFieldFromPackage({
    fieldsByApprovalId = {}, approvalId, fieldId
}) {
    if (approvalId) {
        const fields = fieldsByApprovalId[approvalId] || [];
        return fields.find(({id}) => id === fieldId) || {};
    }
    const allFields = Object.values(fieldsByApprovalId).flat();
    return allFields.find(({id}) => id === fieldId) || {};
}

export const defaultRadioGroupName = 'default';

function getUniqueArray(arr) {
    return arr.filter((item, pos, self) => self.indexOf(item) === pos);
}

export function getGroupsFromPackage(fieldsByApprovalId = {}) {
    const checkboxGroups = [];
    const radioGroups = [];

    Object.values(fieldsByApprovalId).forEach((fields) => {
        fields.forEach((field) => {
            const fieldType = String(field.type || '').toLowerCase();
            if (fieldType === 'checkbox' && field.group) {
                checkboxGroups.push(field.group);
            } else if (fieldType === 'radio' && field.group) {
                radioGroups.push(field.group);
            }
        });
    });

    if (isEmpty(radioGroups)) radioGroups.push(defaultRadioGroupName);

    return {
        checkboxGroups: getUniqueArray(checkboxGroups),
        radioGroups: getUniqueArray(radioGroups)
    };
}

function getConvertedCloneFieldPosition({target, parentElement}) {
    let left = target.getBoundingClientRect().left - parentElement.getBoundingClientRect().left;
    const right = target.getBoundingClientRect().right - parentElement.getBoundingClientRect().right;
    const top = target.getBoundingClientRect().top - parentElement.getBoundingClientRect().top;
    const docScale = parentElement.getBoundingClientRect().width / parseFloat(parentElement.getAttribute('data-page-width'));

    // Catch left positions under 0
    left = left < 0 ? 0 : left;
    // Catch right positions over 0
    left = right > 0 ? left - right : left;

    return {
        left: Math.round(left / docScale),
        top: Math.round(top / docScale)
    };
}

function getConvertedCloneFieldDimensions({target, parentElement, isSquare}) {
    const docScale = parentElement.getBoundingClientRect().width
      / parseFloat(parentElement.getAttribute('data-page-width'));
    const parentHeight = parentElement.getBoundingClientRect().height / docScale;
    const calculatedPosition = getConvertedCloneFieldPosition({target, parentElement});
    const calculatedDimensions = !isSquare ? {
        width: DEFAULT_WIDTH,
        height: DEFAULT_HEIGHT
    } : {
        width: DEFAULT_SQUARE_WIDTH,
        height: DEFAULT_SQUARE_HEIGHT
    };

    // If field is beyond bottom, reduce height
    calculatedDimensions.height = calculatedPosition.top + calculatedDimensions.height > parentHeight - 1
        ? calculatedDimensions.height
          - Math.ceil(calculatedPosition.top + calculatedDimensions.height - parentHeight + 1)
        : calculatedDimensions.height;

    return {
        width: Math.round(calculatedDimensions.width),
        height: Math.round(calculatedDimensions.height)
    };
}

const step = 25;
function checkIsAvailableToPlace({
    left, top, fields, pageHeight, pageWidth
}) {
    return fields.every((field) => (
        (
            (left + DEFAULT_WIDTH < (field.left) || left > (field.left + field.width)) // available by left
            || (top + DEFAULT_HEIGHT < (field.top) || top > (field.top + field.height)) // available by top
        )
        && (top + DEFAULT_HEIGHT < pageHeight && left + DEFAULT_WIDTH < pageWidth) // is inside page
    ));
}

function getAvailablePosition({
    x, y, isAvailable, pageHeight, pageWidth, fields
}) {
    if (isAvailable) return {x, y, isAvailable};
    if (y + DEFAULT_HEIGHT > pageHeight || x + DEFAULT_WIDTH > pageWidth) {
        return {};
    }
    const left = x + step;
    return getAvailablePosition({
        x: left + DEFAULT_WIDTH > pageWidth ? 0 : left,
        y: left + DEFAULT_WIDTH > pageWidth ? y + step : y,
        isAvailable: checkIsAvailableToPlace({
            left, top: y, fields, pageHeight, pageWidth
        }),
        pageWidth,
        pageHeight,
        fields
    });
}

function getCenteredPosition({parentElement, fields, startFromPosition = {}}) {
    const pageWidth = parseFloat(parentElement.getAttribute('data-page-width'));
    const pageHeight = parseFloat(parentElement.getAttribute('data-page-height'));
    const pageOffset = parentElement.getBoundingClientRect();
    const containerElement = parentElement.closest('.documents-renderer-container') || parentElement.parentElement.parentElement.parentElement;
    const containerOffset = containerElement.getBoundingClientRect();
    const scrollOffset = containerElement.querySelector('.documents-renderer-content-wrapper')?.scrollTop || containerElement.scrollTop;
    const scrollOffsetModPage = scrollOffset % pageOffset.height;

    // find place from the center of the page
    const centerY = containerOffset.height / 2 - DEFAULT_HEIGHT / 2 + scrollOffsetModPage;
    const centerPagePosition = {
        x: 0,
        y: centerY
    };

    const centerPos = getAvailablePosition({
        x: startFromPosition.x >= 0 ? startFromPosition.x : centerPagePosition.x,
        y: startFromPosition.y >= 0 ? startFromPosition.y : centerPagePosition.y,
        pageWidth,
        pageHeight,
        fields
    });

    if (centerPos.isAvailable) {
        return {left: centerPos.x, top: centerPos.y};
    }

    // find place from the start of the page
    const startPagePosition = {
        x: 0,
        y: step
    };
    const startPos = getAvailablePosition({
        ...startPagePosition,
        pageWidth,
        pageHeight,
        fields
    });
    return {left: startPos.x, top: startPos.y};
}

function convertPixelsToPercentage(field, page) {
    const leftPercentage = (field.left / page.width) * 100;
    const topPercentage = (field.top / page.height) * 100;

    return {
        leftPercentage,
        topPercentage
    };
}
function convertPercentageToPixels(fieldPercent, page) {
    const leftPixels = (fieldPercent.leftPercentage / 100) * page.width;
    const topPixels = (fieldPercent.topPercentage / 100) * page.height;

    return {
        left: leftPixels,
        top: topPixels
    };
}

export function getDefaultFieldValidation({type}) {
    switch (type) {
        case 'radio':
            return {group: defaultRadioGroupName};
        case 'date':
            return {format: DEFAULT_DATE_FIELD_VALUE};
        case 'textarea':
        case 'paragraph':
        case 'textfield':
        case 'text':
            return {max_length: DEFAULT_FIELD_MAX_LENGTH_VALUE};
        case 'list':
            return {is_required: false};
        case 'checkbox':
        default:
            return {};
    }
}
export function getFieldMaxLengthValue(maxLength) {
    return maxLength > MAX_FIELD_MAX_LENGTH ? MAX_FIELD_MAX_LENGTH : maxLength;
}

export function enumToString(value = []) {
    if (typeof value === 'string') {
        return value.split('/').join('\n');
    }
    return value.join('\n') || '';
}

export function getConfiguredField({
    dimensions,
    customFieldId,
    page,
    type,
    binding,
    signing_type: signingType,
    defaultFontSize = INHERIT_SIZE
}) {
    const field = {
        ...defaultField,
        ...dimensions,
        page,
        type,
        binding,
        ...getDefaultFieldValidation({type}),
        font_size: defaultFontSize,
        name: customFieldId || ''
    };
    if (signingType) field.signing_type = signingType;
    return field;
}

export function getIsCustomFieldType(type) {
    const normalizedType = String(type || '').toLowerCase();
    return normalizedType === 'custom' || normalizedType === 'customfield';
}

export function getResolvedCustomFieldValue({customField, recipientId, customFieldUserValues = {}}) {
    if (!customField) {
        return '';
    }
    const userValue = customFieldUserValues?.[recipientId]?.[customField.id];
    return userValue ?? customField.default_value ?? '';
}

export function configureFieldFromEvent({
    event, customFields, defaultFontSize, customFieldUserValues = {}
}) {
    const dimensions = JSON.parse(event.relatedTarget.getAttribute('dimensions'));
    const type = event.relatedTarget.getAttribute('data-type') || undefined;
    const signingType = event.relatedTarget.getAttribute('data-signing-type') || undefined;
    const binding = event.relatedTarget.getAttribute('data-binding') || undefined;
    const customFieldId = event.relatedTarget.getAttribute('data-custom-field-id');
    const recipientId = event.relatedTarget.getAttribute('data-recipient-id');
    const page = parseInt(event.target.getAttribute('data-page-index'), 10);

    const field = {
        ...getConfiguredField({
            dimensions,
            customFieldId,
            page,
            type,
            binding,
            signing_type: signingType,
            defaultFontSize
        }),
        value: customFieldId ? getResolvedCustomFieldValue({
            customField: customFields.find(({id}) => id === customFieldId),
            recipientId,
            customFieldUserValues
        }) : ''
    };
    return field;
}

function renderIcon({
    firstName,
    lastName,
    recipient,
    type,
    binding,
    signing_type: signingType
}) {
    if (getIsCustomFieldType(type)) return <FiType />;
    if (signingType === 'initials' || signingType === 'paraph') {
        const placeholder = recipient?.placeholder || '';
        const firstNameLetter = firstName ? firstName.substr(0, 1) : placeholder.substr(0, 1);
        const lastNameLetter = lastName ? lastName.substr(0, 1) : placeholder.slice(-1);
        const initials = `${firstNameLetter}${lastNameLetter}`;

        return (
            <div className="initials">
                {initials}
            </div>
        );
    }

    const key = getFieldKey({type, binding, signing_type: signingType});
    return (FIELDS_DATA[key] && FIELDS_DATA[key].icon) || null;
}

function getFieldValue({
    type, binding, value, recipient, groupName, acceptedValues = [], signing_type: signingType
}) {
    const firstName = recipient?.first_name || '';
    const lastName = recipient?.last_name || '';
    const placeholder = recipient?.placeholder || '';
    const isPlaceholderRecipient = !recipient?.email;
    const fullName = `${firstName} ${lastName}`.trim();
    const name = groupName || (isPlaceholderRecipient ? placeholder : fullName);

    if (signingType) {
        return name;
    }

    const apiType = String(type || '').toLowerCase();
    switch (apiType) {
        case 'textfield':
        case 'textarea':
        case 'paragraph':
            return value;
        case 'list':
            return acceptedValues.join(', ');
        case 'label': {
            const apiBinding = String(binding || '').toLowerCase();
            switch (apiBinding) {
                case 'signer_name':
                    return name;
                case 'signer_title':
                    return recipient?.title || '';
                case 'signer_company':
                    return recipient?.organisation || '';
                default:
                    return value || '';
            }
        }
        default:
            return '';
    }
}

export const isTransactionHasFieldsWithUploadSignature = (approvalsByDocumentId = {}) => (
    getAllPackageApprovals(approvalsByDocumentId)
        .some((approval) => approval.is_from_file && !approval.confirmed_at)
);

function getPackageType(path) {
    const transactionPathPattern = new RegExp(`${TRANSACTION_PATH}/[A-Za-z0-9-_]+=*/.*`);
    const templatePathPattern = new RegExp(`${TEMPLATE_PATH}/[A-Za-z0-9-_]+=*/.*`);

    const isTransaction = transactionPathPattern.test(path);
    const isTemplate = templatePathPattern.test(path);
    if (isTransaction) return PACKAGE_TYPE_NAME.TRANSACTION;
    if (isTemplate) return PACKAGE_TYPE_NAME.TEMPLATE;
}

function getDefaultDocumentVisibility(documentId, recipientIds) {
    return {
        document_id: documentId,
        visibilities: recipientIds.map((recipientId) => ({
            recipient_id: recipientId,
            visible: true
        }))
    };
}

function getDefaultDocumentsVisibility(documents = [], signers = [], storedDocumentsVisibility) {
    const recipientIds = signers.map(({recipientId}) => recipientId);
    return documents.map(({id}) => storedDocumentsVisibility.find(({document_id}) => document_id === id)
        || getDefaultDocumentVisibility(id, recipientIds));
}

function getDocumentVisibilityByIndex({
    documents, documentIndex, documentsVisibility
}) {
    const documentId = get(documents, `[${documentIndex}].id`, null);

    if (documentId) {
        return documentsVisibility.find(({document_id}) => document_id === documentId);
    }
}

function getIsVisibilityChangeDisabled({roleId, documentIndex, packageData, approvalsByDocumentId = {}}) {
    const document = packageData.documents?.[documentIndex] || {};
    const approvals = approvalsByDocumentId[document.id] || [];
    return approvals.some((a) => a.recipient_id === roleId);
}

function getVisibilityByRole({documentVisibility, roleId, documentApprovals = []}) {
    if (!isEmpty(documentVisibility)) {
        const entry = (documentVisibility.visibilities || []).find((v) => v.recipient_id === roleId);
        const isVisible = entry ? entry.visible : true;
        const hasFields = documentApprovals.some((a) => a.recipient_id === roleId);
        return isVisible || hasFields;
    }

    return true;
}

function checkIsAcceptedRecipient({approval, approvalFields = [], senderId}) {
    return approval
        && approval.signing_type === 'accept'
        && isEmpty(approvalFields)
        && approval.recipient_id !== senderId;
}

function getAcceptedRecipientsIdsForDocument({documentApprovals, fieldsByApprovalId = {}, senderId}) {
    if (!isEmpty(documentApprovals)) {
        return documentApprovals
            .filter((approval) => checkIsAcceptedRecipient({
                approval,
                approvalFields: fieldsByApprovalId[approval.id] || [],
                senderId
            }))
            .map((approval) => approval.recipient_id);
    }
    return [];
}

export function checkIsAcceptedDocument({documentApprovals = [], fieldsByApprovalId = {}}) {
    if (isEmpty(documentApprovals)) return false;
    return documentApprovals.every((a) => (
        a.signing_type === 'accept' && isEmpty(fieldsByApprovalId[a.id])
    ));
}

export function getIsAcceptOnlyRecipient({documentApprovals = [], fieldsByApprovalId = {}, recipientId}) {
    if (!recipientId) return false;
    return documentApprovals.some((approval) => (
        approval.recipient_id === recipientId
        && approval.signing_type === 'accept'
        && isEmpty(fieldsByApprovalId[approval.id])
    ));
}

export function getDocumentInfo({
    sender, document, packageData, documentApprovals = [], fieldsByApprovalId = {}
}) {
    const isAdaDocument = !isEmpty(document) && get(packageData, 'settings.ceremony.ada') && document.tagged;
    const isConsentDocument = !isEmpty(document) && packageData.consent === document.id;
    const isAcceptOnlyDocument = checkIsAcceptedDocument({
        documentApprovals,
        fieldsByApprovalId,
        senderId: sender?.id
    });

    return {
        isAdaDocument,
        isConsentDocument,
        isAcceptOnlyDocument
    };
}

export function getDisabledSignaturesErrorMessage({
    packageData, documentId, documentSigners = [], selectedApproval,
    documentApprovals = [], fieldsByApprovalId = {}, recipientId
}) {
    const document = packageData.documents?.find(({id}) => id === documentId) || {};
    const isSignedDocument = documentSigners.some(({hasSignedApprovals, approvals}) => {
        const isActiveDocumentSigned = approvals.some((approval) => approval.documentId === documentId && approval.confirmed_at != null);
        return hasSignedApprovals && isActiveDocumentSigned;
    });

    const recipients = packageData.recipients || [];
    const sender = recipients.find((r) => getIsSenderRecipient(r, packageData.owner_id)) || {};
    const {isAdaDocument, isConsentDocument} = getDocumentInfo({
        sender, document, packageData, documentApprovals, fieldsByApprovalId
    });
    const isAcceptOnlyRecipient = getIsAcceptOnlyRecipient({documentApprovals, fieldsByApprovalId, recipientId});
    let message = '';

    if (isAcceptOnlyRecipient) {
        message = 'page.designerValidation.signaturesDisabled';
    } else if (isSignedDocument) {
        message = 'esl.error.forbidden.cannotEditDocumentWithApprovals';
    } else if (isConsentDocument) {
        message = 'esl.error.validation.defaultConsent.modifyingApproval';
    } else if (isAdaDocument) {
        message = 'esl.generic.ada_document_tagged';
    } else if (selectedApproval && !selectedApproval.id) {
        message = 'page.designerValidation.disabledByNoSelectedSignatureOnDocument';
    }
    return message;
}

function getAvailableSignatureFields({
    documentApprovals = [],
    signatureFields,
    features,
    selectedSigner = {},
    defaultSignatureType
}) {
    const isOptionalSignatureActive = get(features, 'optionalSignature', false);
    const isMobileActive = get(features, 'mobileCapture', false);
    const selectedDefaultSignatureType = getDefaultSignatureType(isMobileActive, defaultSignatureType);
    const signerSignatureApprovals = documentApprovals.filter((a) => (
        a.recipient_id === selectedSigner.recipientId
        && a.signing_type
        && a.signing_type !== 'accept'
    ));
    const hasPlacedCaptureSignature = signerSignatureApprovals.some((a) => a.signing_type === 'capture');

    return signatureFields.reduce((acc, field) => {
        const isMobileEntry = field.signing_type === 'mobile_capture';
        if ((isMobileEntry && !isMobileActive) || (field.isOptional && !isOptionalSignatureActive)) {
            return acc;
        }
        if (hasPlacedCaptureSignature && isMobileEntry) return acc;
        if (field.isOptional && isOptionalSignatureActive && !field.signing_type) {
            acc.push({...field, signing_type: selectedDefaultSignatureType});
            return acc;
        }
        acc.push(field);
        return acc;
    }, []);
}
let scrollUpdateTimeout;
let resetScrollPosition = true;
function scrollByDrag(scrollbar, el) {
    clearTimeout(scrollUpdateTimeout);

    if (!el || !scrollbar) return;

    const offsetToStart = 5;
    const xDelay = 1000;
    const yDelay = 1800;

    const elRect = el.getBoundingClientRect();

    const {bounding, offset, limit} = scrollbar || {};

    const scrollViewportHeight = bounding.bottom - bounding.top;
    const scrollViewportWidth = bounding.right - bounding.left;

    if (limit.y > 0 && elRect.top < bounding.top + offsetToStart && offset.y > 0) {
        scrollbar.scrollTo(offset.x, 0, (offset.y / scrollViewportHeight) * yDelay);
        resetScrollPosition = true;
    } else if (limit.x > 0 && elRect.left < bounding.left + offsetToStart && offset.x > 0) {
        scrollbar.scrollTo(0, offset.y, (limit.x / scrollViewportWidth) * xDelay);
        resetScrollPosition = true;
    } else if (limit.x > 0 && elRect.right > bounding.right - offsetToStart && offset.x < limit.x) {
        scrollbar.scrollTo(limit.x, offset.y, ((limit.x - offset.x) / scrollViewportWidth) * xDelay);
        resetScrollPosition = true;
    } else if (limit.y > 0 && elRect.bottom > bounding.bottom - offsetToStart && offset.y < limit.y) {
        scrollbar.scrollTo(offset.x, limit.y, ((limit.y - offset.y) / scrollViewportHeight) * yDelay);
        resetScrollPosition = true;
    } else if (resetScrollPosition) {
        scrollbar.scrollTo(offset.x, offset.y);
        resetScrollPosition = false;
    } else {
        scrollUpdateTimeout = setTimeout(() => {
            scrollbar.scrollTo(offset.x, offset.y);
        }, 100);
    }
}

function scrollToBottom(scrollNodeRef) {
    setTimeout(() => {
        if (scrollNodeRef?.current) {
            // eslint-disable-next-line no-param-reassign
            scrollNodeRef.current.getScrollElement().scrollTop = 9999;
        }
    }, 0);
}

const configDuplicatedField = ({
    field, approval, documentId, prevPageIndex, pageIndex, packageData, dispatch
}) => {
    const isRadio = String(field.type || '').toLowerCase() === 'radio';
    const patchedGroup = isRadio && !field.group ? defaultRadioGroupName : field.group;
    const pageTarget = document.querySelector(`[data-doc-page-id="${documentId}-${pageIndex}"]`);
    const pageFields = dispatch(getPageFields(documentId, pageIndex));
    const isTheSamePage = prevPageIndex === pageIndex && prevPageIndex >= 0;
    return {
        ...field,
        approvalId: approval.id,
        packageId: packageData.id,
        documentId,
        id: undefined,
        name: getIsCustomFieldType(field.type) ? field.name : '',
        recipient_id: approval.recipient_id,
        group: patchedGroup,
        ...getCenteredPosition({
            parentElement: pageTarget,
            fields: pageFields,
            startFromPosition: isTheSamePage ? {x: field.left, y: field.top} : {}
        })
    };
};

const configDuplicatedFieldForOtherPages = ({
    field, approval, documentId, packageData, sourcePage, targetPage
}) => {
    const isRadio = String(field.type || '').toLowerCase() === 'radio';
    const patchedGroup = isRadio && !field.group ? defaultRadioGroupName : field.group;
    const targetFieldPosition = convertPercentageToPixels(convertPixelsToPercentage(field, sourcePage), targetPage);
    return {
        ...field,
        approvalId: approval.id,
        packageId: packageData.id,
        documentId,
        id: undefined,
        name: getIsCustomFieldType(field.type) ? field.name : '',
        recipient_id: approval.recipient_id,
        group: patchedGroup,
        ...targetFieldPosition
    };
};
export {
    getColorsForSigners,
    getTags,
    getRecipientIndexById,
    getConvertedCloneFieldPosition,
    getConvertedCloneFieldDimensions,
    getCenteredPosition,
    defaultApproval,
    defaultField,
    getFieldById,
    getFieldValue,
    getFieldTypeName,
    modifyFieldFromResize,
    modifyFieldFromDrop,
    renderIcon,
    getDocumentVisibilityByIndex,
    getVisibilityByRole,
    getPackageType,
    getAcceptedRecipientsIdsForDocument,
    scrollByDrag,
    getDefaultDocumentVisibility,
    getDefaultDocumentsVisibility,
    getAvailableSignatureFields,
    getIsVisibilityChangeDisabled,
    scrollToBottom,
    configDuplicatedField,
    configDuplicatedFieldForOtherPages
};
